import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import * as authService from "../../services/authService";
import * as masterServices from "../../services/masterServices";
import * as commonAPI from "../../services/commonServices";
import LocalStorageUtil from "../../util/LocalStorageUtil";
import { LocalStorageConstants, LocalStorageValueConstants } from "../../constants/ApplicationConstants/LocalStorageConstants";
import { RouteConstants } from "../../constants/ApplicationConstants/RouteConstants";
import { CustomNotification } from "../../util/CustomNotification";
import { CustomerSideNextScreenConstants } from "../../constants/generalConstants";
import { ErrorMessageConstants } from "../../constants/MessageConstants/ErrorMessageConstants";
import Authentication from "../../util/Authentication";
import { Base64StringEncoding } from "../../util/Cryptography";
import useValue from "../../hooks/useValue";
import { ModuleAndIdMapping, Modules, Operations } from "../../constants/DatabaseConstants/MasterConstant";

import type { AuthUserAPIResponse } from "../../shared/interface/auth/User.interface";
import type FixMeLater from "../../shared/types/fixMeLater.type";
import UIConfiguration from "../../util/UIConfiguration";
import { MasterFiltersConstant } from "../../constants/ApplicationConstants/MasterConstant";
import { updateMasterFilters, updateOpenCount } from "../../appRedux/reducers/Common";
import { getAllOpenSupportQueryCount } from "../../services/supportQuery";

export const useSignIn = () => {
  /* CONSTANT */
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const searchParamNextScreen = searchParams.get("nextScreen");

  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);

  /* STATE */
  const [nextScreen, setNextScreen] = useState<null | FixMeLater>(searchParamNextScreen ? +searchParamNextScreen : null);
  const [isUserActive, setIsUserActive] = useState(true);
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  /* HELPER FUNCTION */

  const getCustomerId = async () => {
    try {
      const getLocationMaster = await masterServices.getOneMasterListById(ModuleAndIdMapping[Modules.CUSTOMER_MASTER]);
      const customerId = getLocationMaster?.["AllRecordsList"]?.[0]?.id ?? null;
      return customerId;
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const getLocationList = async (customerId: number) => {
    try {
      if (!customerId) return;
      return await commonAPI.getLocationListByCustomerId(customerId);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const updateTopBarFiltersHelper = async (response: AuthUserAPIResponse) => {
    try {
      const customerId = await getCustomerId();

      const locationListResponse = await getLocationList(customerId);
      const getLocationId = locationListResponse?.data?.[0]?.id || null;

      const dataObj = {
        [MasterFiltersConstant.CUSTOMER_ID]: customerId,
        [MasterFiltersConstant.LOCATION_ID]: getLocationId
      };

      LocalStorageUtil.localStorageUpdateData(LocalStorageConstants.MASTER_FILTERS, dataObj);
      dispatch(updateMasterFilters(dataObj));
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  /* a helper function which populates the local storage with required data on success login. */
  const applicationLoginHelper = async (response: AuthUserAPIResponse) => {
    /* setting values in localstorage */
    Authentication.addExpiryTimeToLocalStorage();

    LocalStorageUtil.localStorageSetItem(LocalStorageConstants.USER_DATA, response.user!);
    LocalStorageUtil.localStorageSetItem(LocalStorageConstants.IS_USER_ACTIVE, LocalStorageValueConstants.TRUE);
    LocalStorageUtil.localStorageSetItem(LocalStorageConstants.IS_TOKEN_EXPIRED, LocalStorageValueConstants.FALSE);

    /* topbar filter update */
    await updateTopBarFiltersHelper(response);

    /*support-query openCount */
    if (Authentication.hasModuleAndOperationAccess(response?.user, Modules.SUPPORT_QUERY, Operations.READ)) {
      await fetchOpenCount();
    }

    /* theme update */
    UIConfiguration.updateUIConfig(response.user!);

    CustomNotification.showSuccessMessage(response.message);

    navigate(RouteConstants.DEFAULT_HOME_PAGE);

    return;
  };

  /* a helper function which decides which screen to go next, based on API response. */
  const nextScreenHelper = async (response: AuthUserAPIResponse) => {
    switch (response.nextScreen as CustomerSideNextScreenConstants) {
      case CustomerSideNextScreenConstants.UPDATE_PASSWORD: {
        handleNextScreen(CustomerSideNextScreenConstants.UPDATE_PASSWORD);
        break;
      }

      case CustomerSideNextScreenConstants.FORGOT_PASSWORD: {
        handleNextScreen(CustomerSideNextScreenConstants.FORGOT_PASSWORD);
        break;
      }

      case CustomerSideNextScreenConstants.APPLICATION_LOGIN: {
        await applicationLoginHelper(response);
        break;
      }
      default: {
        throw new Error(ErrorMessageConstants.ERROR_LOGGING_IN);
      }
    }
  };

  /* HANDLERS */
  const handleNextScreen = (value: CustomerSideNextScreenConstants | null) => setNextScreen(value);

  const handleForgotPasswordScreen = () => {
    handleNextScreen(CustomerSideNextScreenConstants.FORGOT_PASSWORD);
  };

  const handleShowUserSessionExpiredModal = (val: boolean) => {
    localStorage.clear();
  };

  const fetchOpenCount = async () => {
    try {
      const response = await getAllOpenSupportQueryCount();
      const openCountFromAPI = response?.data?.openCount ?? 0;

      dispatch(updateOpenCount(openCountFromAPI));
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const handleSubmit = async (data: { username: string; password: string }) => {
    try {
      handleEnableLoading();

      const response = await authService.signIn(data.username, data.password);
      nextScreenHelper(response);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableLoading();
    }
  };

  const handleUserActiveOk = () => {
    setIsUserActive(true);
    localStorage.clear();
  };

  const handleTokenExpiredOk = () => {
    setIsTokenExpired(false);
    localStorage.clear();
  };

  /* MEMO */
  useMemo(() => {
    /* 
         when back button is pressed by the child components, we want to display the sign-in page, by clearing the
         nextScreen state.
      */
    if (searchParamNextScreen) handleNextScreen(+searchParamNextScreen);
    if (!searchParamNextScreen) handleNextScreen(null);
  }, [searchParamNextScreen]);

  /* EFFECT */
  /* useEffect related to multiple-tab logout/login */
  useEffect(() => {
    /* (to handle multiple tabs) we register an event listener, example -> if all tabs are on sign-in page and one tab logs in we check if necessary
     local-storage is set and move all tabs to home=page.
  */
    const fn = (e: StorageEvent) => {
      const childFn = async () => {
        /* we only want to change urls when token or when user becomes idle */
        /* e.key when null means clear local storage event fired. */
        if (e.key === Base64StringEncoding.encode(LocalStorageConstants.IS_TOKEN_EXPIRED) || e.key === Base64StringEncoding.encode(LocalStorageConstants.IS_USER_ACTIVE) || !e.key) {
          const userSession = Authentication.isUserSessionActive();
          if (userSession) window.location.assign(RouteConstants.DEFAULT_HOME_PAGE);
          if (!userSession) window.location.assign("/");
        }
      };

      childFn();
    };

    window.addEventListener("storage", fn, false);
    return () => window.removeEventListener("storage", fn, false);
  }, []);

  /* useEffect related to multiple-tab logout/login */
  useEffect(() => {
    /* 
     when this component is mounted, we check if localstorage has all necessary keys. If all correct we move to home page. 
     If not we open necessary modal windows (idle-timeout or token-expired) on sign-in (current/this) component
  */
    if (Authentication.isUserSessionActive()) {
      navigate(RouteConstants.DEFAULT_HOME_PAGE);
      return;
    }
    const isUserActive = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.IS_USER_ACTIVE);
    const isTokenExpired = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.IS_TOKEN_EXPIRED);

    if (isUserActive === LocalStorageValueConstants.FALSE) setIsUserActive(false);
    if (isTokenExpired === LocalStorageValueConstants.TRUE) setIsTokenExpired(true);
  }, []);

  return { loading, handleSubmit, nextScreen, handleNextScreen, nextScreenHelper, handleUserActiveOk, handleTokenExpiredOk, isUserActive, isTokenExpired, handleForgotPasswordScreen, handleShowUserSessionExpiredModal };
};
